import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import TermsAndConditions from '../TermsAndConditions.tsx';
import { useNavigate } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const validatePassword = (pass: string) => {
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    return hasUpperCase && hasNumber;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    if (mode === 'register') {
      if (!validatePassword(password)) {
        toast.error('La contraseña debe contener al menos una mayúscula y un número');
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Las contraseñas no coinciden');
        setLoading(false);
        return;
      }
      if (!acceptedTerms) {
        toast.error('Debes aceptar los Términos y Condiciones para crear una cuenta.');
        setLoading(false);
        return;
      }
    }
    
    try {
      let response;
      
      if (mode === 'login') {
        response = await supabase.auth.signInWithPassword({
          email,
          password
        });
      } else if (mode === 'register') {
        response = await supabase.auth.signUp({
          email,
          password
        });
      } else {
        response = await supabase.auth.resetPasswordForEmail(email);
      }

      if (response.error) {
        throw new Error(response.error.message);
      }

      // Manejo de respuesta exitosa
      if (mode === 'login') {
        toast.success('Sesión iniciada correctamente');
        onClose();
      } else if (mode === 'register') {
        toast.success('¡Cuenta creada correctamente!');
        navigate('/dashboard');
        onClose();
      } else {
        toast.success('Se ha enviado un enlace a tu correo');
      }

    } catch (error) {
      console.error('Error de autenticación:', error);
      toast.error(error instanceof Error ? error.message : 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      
      if (error) {
        throw error;
      }
      
      // No necesitamos cerrar el modal aquí, ya que el usuario será redirigido a Google
      
    } catch (error) {
      console.error('Error al iniciar sesión con Google:', error);
      toast.error('Error al iniciar sesión con Google. Por favor, inténtalo de nuevo.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Función para manejar la aceptación desde el modal de términos
  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
    setShowTermsModal(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-[0_0_30px_rgba(0,0,0,0.3)] p-8 text-white transition-all">
          {/* Efecto de resplandor */}
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/30 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-purple-500/30 rounded-full blur-[100px] animate-pulse delay-300" />

          {/* Botón de cerrar */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>

          <div className="relative">
            {/* Título con efecto futurista centrado */}
            <h2 className="text-3xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-500 text-center relative">
              {mode === 'login' ? 'Iniciar Sesión' : mode === 'register' ? 'Crear Cuenta' : 'Recuperar Contraseña'}
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"></div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-purple-400/50 rounded-full blur-sm animate-pulse"></div>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 text-white placeholder-gray-400 outline-none transition-all"
                  placeholder="tu@email.com"
                />
              </div>

              {mode !== 'reset' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500/50 
                               focus:ring-2 focus:ring-blue-500/20 text-white placeholder-gray-400 outline-none transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              )}

              {mode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-500/50 
                               focus:ring-2 focus:ring-blue-500/20 text-white placeholder-gray-400 outline-none transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    La contraseña debe contener al menos una mayúscula y un número
                  </p>
                </div>
              )}

              {/* Checkbox de Términos y Condiciones (solo en modo registro) */}
              {mode === 'register' && (
                <div className="flex items-center space-x-2 mt-4">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 bg-white/10"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-300">
                    Acepto los{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-blue-400 hover:text-blue-300 underline focus:outline-none"
                    >
                      Términos y Condiciones
                    </button>
                  </label>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-lg font-medium text-white transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed relative group"
              >
                <span className="relative z-10">
                  {loading ? 'Procesando...' : mode === 'login' ? 'Iniciar sesión' : mode === 'register' ? 'Crear cuenta' : 'Enviar enlace'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/50 to-purple-600/50 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              
              {mode === 'login' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setMode('reset')}
                    className="text-sm hover:text-blue-400 transition-colors text-gray-400"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}
            </form>
            
            {/* Separador */}
            <div className="relative my-6 flex items-center">
              <div className="flex-grow border-t border-white/10"></div>
              <span className="flex-shrink mx-4 text-gray-400 text-sm">O continúa con</span>
              <div className="flex-grow border-t border-white/10"></div>
            </div>
            
            {/* Botón de Google */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full py-3 px-4 mb-4 bg-white rounded-lg font-medium text-gray-800 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 relative group hover:shadow-md"
            >
              <img src="/img/google.png" alt="Google" className="w-5 h-5" />
              <span>
                {googleLoading ? 'Conectando...' : 'Continuar con Google'}
              </span>
            </button>

            <div className="flex flex-col items-center gap-2 text-sm text-gray-400 mt-4">
              {mode === 'login' && (
                <div className="flex items-center gap-2">
                  <span>¿No tienes cuenta?</span>
                  <button
                    type="button"
                    onClick={() => setMode('register')}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Regístrate
                  </button>
                </div>
              )}

              {mode === 'register' && (
                <div className="flex items-center gap-2">
                  <span>¿Ya tienes cuenta?</span>
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Inicia sesión
                  </button>
                </div>
              )}

              {mode === 'reset' && (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Volver al inicio de sesión
                </button>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>

      {/* Modal para mostrar Términos y Condiciones */}
      <Dialog
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        className="relative z-[60]"
      >
        {/* Fondo oscuro */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

        {/* Contenedor del contenido */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-2xl bg-gray-900 text-left align-middle shadow-xl transition-all border border-white/20">
             <button
                onClick={() => setShowTermsModal(false)}
                className="absolute right-4 top-4 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/70 transition-colors z-10"
                aria-label="Cerrar términos y condiciones"
              >
                <X className="w-5 h-5 text-gray-300" />
              </button>
              {/* Pasar la función handleAcceptTerms como prop */}
              <TermsAndConditions onAccept={handleAcceptTerms} />
          </Dialog.Panel>
        </div>
      </Dialog>
    </Dialog>
  );
}